import { APIResponseType } from "../../../types/generics/APIResponse.type";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import {
  createPostApiHook,
  createGetApiHook,
  createPatchApiHook,
  createPutApiHook,
  createDeleteApiHook,
} from "../apiHooks";

// Types - Matching API endpoint structure: /api/v1/airbank/register-ait-dna
export interface AirbankRegistrationPayload {
  name: string; // AI Twin name (required)
  personality?: string; // Personality description
  language?: string; // Language code (e.g., "en", "es")
  custom_greetings?: string; // Custom greeting message
  avatar_url?: string; // Primary avatar image URL (first image when multiple)
  image_urls?: string[]; // Multiple image URLs for AI-DNA gallery
  audio_url?: string; // Audio recording URL (uploaded via uploadFile)
  video_url?: string; // Video recording URL (uploaded via uploadFile)
  mbti?: string; // MBTI personality type
  allowed_use_cases?: string[]; // Fields in which the AIT may be used
  prohibited_use_agreed?: boolean; // User acknowledged prohibited-use disclaimer
}

export interface AirbankStatus {
  is_registered: boolean;
  is_verified: boolean;
  registration_date?: string;
  verification_date?: string;
  verification_status: "pending" | "verified" | "rejected" | "expired";
  badge_issued: boolean;
  badge_url?: string;
  dna_hash?: string;
  entity_id?: string;
  rejection_reason?: string;
}

export interface AirbankVerificationResponse {
  success: boolean;
  message: string;
  verification_status: AirbankStatus["verification_status"];
  badge_issued?: boolean;
  badge_url?: string;
}

// Dedicated Airbank API endpoints - matches /api/v1/airbank/register-ait-dna
// Note: talent-id is sent as header, not in URL path
const registerAirbankDNAEndpoint = () => `/airbank/register-ait-dna`;

// Create the Airbank registration hook - separate from AI Twin registration
const useRegisterAirbankDNAHook = createPostApiHook<
  AirbankRegistrationPayload,
  { headers: { "talent-id": string } },
  APIResponseType<any>
>(registerAirbankDNAEndpoint, {
  isFormDataMethod: false,
});

/**
 * Custom hook for registering AI Twin DNA in Airbank
 * This is a completely separate endpoint from AI Twin registration
 * Airbank DNA registration is independent and doesn't affect AI Twin setup
 */
/**
 * Custom hook for registering AI Twin DNA in Airbank
 * Endpoint: POST /api/v1/airbank/register-ait-dna
 * Requires: talent-id header
 * Payload: { name, personality?, language?, custom_greetings?, avatar_url?, audio_url?, video_url? }
 */
export const useRegisterAirbankDNA = (options: {
  key: any[];
  params: { talentId: number };
}) => {
  const { key, params } = options;
  const { talentId } = params;
  const queryClient = useQueryClient();

  // Use dedicated Airbank registration endpoint with talent-id header
  const {
    update: registerDNA,
    isLoading,
    isError,
    error,
  } = useRegisterAirbankDNAHook({
    key: [...key, "airbank-register-dna"],
    params: {
      headers: {
        "talent-id": talentId.toString(),
      },
    },
  });

  return {
    register: async (
      payload: AirbankRegistrationPayload,
      callbacks?: {
        onSuccess?: (data: any) => void;
        onError?: (error: any) => void;
      },
    ) => {
      try {
        // Validate required fields
        if (!payload.name?.trim()) {
          throw new Error("AI Twin name is required");
        }

        // Validate URLs if provided
        if (payload.avatar_url && !isValidUrl(payload.avatar_url)) {
          throw new Error("Invalid avatar URL. Please upload the photo again.");
        }
        if (payload.audio_url && !isValidUrl(payload.audio_url)) {
          throw new Error("Invalid audio URL. Please upload the audio again.");
        }
        if (payload.video_url && !isValidUrl(payload.video_url)) {
          throw new Error("Invalid video URL. Please upload the video again.");
        }

        // Build Airbank registration payload matching API structure
        const airbankPayload: AirbankRegistrationPayload = {
          name: payload.name.trim(),
          personality: payload.personality?.trim(),
          language: payload.language?.trim(),
          custom_greetings: payload.custom_greetings?.trim(),
          avatar_url: payload.avatar_url,
          image_urls:
            payload.image_urls && payload.image_urls.length > 0
              ? payload.image_urls
              : undefined,
          audio_url: payload.audio_url,
          video_url: payload.video_url,
          mbti: payload.mbti?.trim(),
          allowed_use_cases:
            payload.allowed_use_cases && payload.allowed_use_cases.length > 0
              ? payload.allowed_use_cases
              : undefined,
          prohibited_use_agreed: payload.prohibited_use_agreed,
        };

        console.log("[Airbank] Registering DNA with payload:", airbankPayload);

        const result = await registerDNA(airbankPayload, {
          onSuccess: (data: any) => {
            // Invalidate queries to refresh Airbank status and profile data
            queryClient.invalidateQueries({
              queryKey: ["talent-profile-airbank-status", talentId],
            });
            queryClient.invalidateQueries({
              queryKey: ["airbank-my-ait", talentId],
            }); // Invalidate shared query used by all components
            queryClient.invalidateQueries({ queryKey: ["user-profile"] });
            queryClient.invalidateQueries({
              queryKey: ["talent-profile-ai-twin-settings", talentId],
            });
            queryClient.invalidateQueries({ queryKey: ["talent", talentId] }); // Invalidate talent profile so sidebar updates ait_dna status
            callbacks?.onSuccess?.(data);
          },
          onError: (error: any) => {
            console.error("[Airbank] Registration failed:", error);
            const errorMessage =
              error?.response?.data?.message ||
              error?.message ||
              "Failed to register with Airbank. Please try again.";
            callbacks?.onError?.(new Error(errorMessage));
          },
        });
        return result;
      } catch (error: any) {
        console.error("[Airbank] Registration error:", error);
        callbacks?.onError?.(error);
        throw error;
      }
    },
    isLoading,
    isError,
    error,
  };
};

// Helper function to validate URLs
const isValidUrl = (url: string): boolean => {
  try {
    const urlObj = new URL(url);
    return urlObj.protocol === "http:" || urlObj.protocol === "https:";
  } catch {
    return false;
  }
};

// AIT DNA by ID - for update (talent's own registration)
export type AitDnaUpdateParams = {
  id: number;
  headers?: { "talent-id": string };
};
const aitDnaByIdEndpoint = (params: { id: number }) =>
  `/airbank/update-ait-dna/${params.id}`;

const usePutAitDnaHook = createPutApiHook<
  Partial<AirbankRegistrationPayload>,
  AitDnaUpdateParams,
  APIResponseType<any>
>(aitDnaByIdEndpoint, { isFormDataMethod: false });

// AIT delete by ID - matches DELETE /api/v1/airbank/ait/{id}
const aitDeleteByIdEndpoint = (params: { id: number }) =>
  `/airbank/ait/${params.id}`;

const useDeleteAitDnaHook = createDeleteApiHook<
  Record<string, never>,
  AitDnaUpdateParams,
  APIResponseType<any>
>(aitDeleteByIdEndpoint, { isFormDataMethod: false });

/**
 * Update (PUT) the current talent's AI-DNA registration.
 * Endpoint: PUT /api/v1/airbank/update-ait-dna/:id
 * Requires: talent-id header.
 */
export const useUpdateAitDna = (options: {
  key: any[];
  params: { talentId: number; aitDnaId: number };
}) => {
  const { key, params } = options;
  const { talentId, aitDnaId } = params;
  const queryClient = useQueryClient();

  const {
    update: putAitDna,
    isLoading,
    isError,
    error,
  } = usePutAitDnaHook({
    key: [...key, "airbank-put-ait-dna"],
    params: {
      id: aitDnaId,
      headers: { "talent-id": talentId.toString() },
    },
  });

  return {
    update: async (
      payload: Partial<AirbankRegistrationPayload>,
      callbacks?: {
        onSuccess?: (data: any) => void;
        onError?: (error: any) => void;
      },
    ) => {
      try {
        if (payload.avatar_url && !isValidUrl(payload.avatar_url)) {
          throw new Error("Invalid avatar URL.");
        }
        if (payload.image_urls && Array.isArray(payload.image_urls)) {
          for (const url of payload.image_urls) {
            if (!isValidUrl(url)) {
              throw new Error(`Invalid image URL: ${url}`);
            }
          }
        }
        if (payload.audio_url && !isValidUrl(payload.audio_url)) {
          throw new Error("Invalid audio URL.");
        }
        if (payload.video_url && !isValidUrl(payload.video_url)) {
          throw new Error("Invalid video URL.");
        }
        const result = await putAitDna(payload, {
          onSuccess: (data: any) => {
            queryClient.invalidateQueries({
              queryKey: ["talent-profile-airbank-status", talentId],
            });
            queryClient.invalidateQueries({
              queryKey: ["airbank-my-ait", talentId],
            }); // Invalidate shared query used by all components
            queryClient.invalidateQueries({ queryKey: ["user-profile"] });
            queryClient.invalidateQueries({
              queryKey: ["talent-profile-ai-twin-settings", talentId],
            });
            queryClient.invalidateQueries({ queryKey: ["talent", talentId] }); // Invalidate talent profile so sidebar updates ait_dna status
            callbacks?.onSuccess?.(data);
          },
          onError: (err: any) => {
            const msg =
              err?.response?.data?.message ||
              err?.message ||
              "Failed to update AI-DNA.";
            callbacks?.onError?.(new Error(msg));
          },
        });
        return result;
      } catch (err: any) {
        callbacks?.onError?.(err);
        throw err;
      }
    },
    isLoading,
    isError,
    error,
  };
};

// GET endpoint for fetching current talent's AI Twin and AI-DNA data
const getMyAitEndpoint = () => `/airbank/my-ait`;

// Create the GET hook for my-ait endpoint
// Response structure: { message: "Success", data: [{ twin: {...}, ait_dna: {...}, categories: [...], portfolio_links: [...] }] }
const useGetMyAitHook = createGetApiHook<
  APIResponseType<
    Array<{
      twin?: any;
      ait_dna?: any;
      categories?: string[];
      portfolio_links?: any[];
      [k: string]: any;
    }>
  >,
  { headers: { "talent-id": string } }
>(getMyAitEndpoint);

/**
 * Get the current talent's AI Twin and AI-DNA data.
 * Endpoint: GET /api/v1/airbank/my-ait
 * Requires: talent-id header.
 * Returns: { twin: {...}, ait_dna: {...}, categories: [...], portfolio_links: [...] }
 */
export const useGetMyAit = (options: {
  key: any[];
  params: { talentId: number };
  options?: {
    enabled?: boolean;
    retry?: number;
    staleTime?: number;
  };
}) => {
  const { key, params, options: queryOptions } = options;
  const { talentId } = params;

  const { data, isLoading, isError, error, refetch } = useGetMyAitHook({
    key: [...key, "airbank-my-ait"],
    params: {
      headers: {
        "talent-id": talentId.toString(),
      },
    },
    options: {
      enabled: queryOptions?.enabled !== false && !!talentId,
      retry: queryOptions?.retry ?? 2,
      staleTime: queryOptions?.staleTime ?? 30000, // 30 seconds
      refetchOnMount: false, // Don't refetch if data is fresh (within staleTime)
      refetchOnWindowFocus: false, // Don't refetch on window focus if data is fresh
    },
  });

  return {
    data,
    isLoading,
    isError,
    error,
    refetch,
  };
};

/**
 * Delete the current talent's AI-DNA registration.
 * Endpoint: DELETE /api/v1/airbank/ait-dna/:id
 * Requires: talent-id header.
 */
export const useDeleteAitDna = (options: {
  key: any[];
  params: { talentId: number; aitDnaId: number };
}) => {
  const { key, params } = options;
  const { talentId, aitDnaId } = params;
  const queryClient = useQueryClient();

  const {
    update: deleteAitDna,
    isLoading,
    isError,
    error,
  } = useDeleteAitDnaHook({
    key: [...key, "airbank-delete-ait-dna"],
    params: {
      id: aitDnaId,
      headers: { "talent-id": talentId.toString() },
    },
  });

  return {
    delete: async (callbacks?: {
      onSuccess?: (data: any) => void;
      onError?: (error: any) => void;
    }) => {
      try {
        const result = await deleteAitDna(
          {},
          {
            onSuccess: (data: any) => {
              queryClient.invalidateQueries({
                queryKey: ["talent-profile-airbank-status", talentId],
              });
              queryClient.invalidateQueries({
                queryKey: ["airbank-my-ait", talentId],
              }); // Invalidate shared query used by all components
              queryClient.invalidateQueries({ queryKey: ["user-profile"] });
              queryClient.invalidateQueries({
                queryKey: ["talent-profile-ai-twin-settings", talentId],
              });
              queryClient.invalidateQueries({ queryKey: ["talent", talentId] }); // Invalidate talent profile so sidebar updates ait_dna status
              callbacks?.onSuccess?.(data);
            },
            onError: (err: any) => {
              const msg =
                err?.response?.data?.message ||
                err?.message ||
                "Failed to delete AI-DNA.";
              callbacks?.onError?.(new Error(msg));
            },
          },
        );
        return result;
      } catch (err: any) {
        callbacks?.onError?.(err);
        throw err;
      }
    },
    isLoading,
    isError,
    error,
  };
};

/**
 * Custom hook for getting Airbank status
 * Note: Status is now retrieved from talent profile data (twin object) via useAirbankStatus hook
 * This hook is kept for backward compatibility but is no longer used
 * @deprecated Use useAirbankStatus from hooks/useAirbankStatus.ts instead
 */
export const useGetAirbankStatus = (options: {
  key: any[];
  params: { talentId: number };
  options?: any;
}) => {
  // This is a placeholder - actual status is retrieved from talent profile
  // See useAirbankStatus hook in hooks/useAirbankStatus.ts
  return {
    data: null,
    isLoading: false,
    isError: false,
    error: null,
  };
};

/**
 * Custom hook for verifying AI Twin in Airbank
 * Note: Verification is handled by the backend through the update-twin endpoint
 * This hook is kept for backward compatibility but may not be needed
 * @deprecated Verification is handled automatically by backend
 */
export const useVerifyAirbankTwin = (options: {
  key: any[];
  params: { talentId: number };
}) => {
  return {
    verify: async (callbacks?: {
      onSuccess?: (data: any) => void;
      onError?: (error: any) => void;
    }) => {
      console.warn(
        "[Airbank] Verification is handled automatically by the backend",
      );
      callbacks?.onSuccess?.({
        success: true,
        message: "Verification handled by backend",
      });
    },
    isLoading: false,
    isError: false,
    error: null,
  };
};

/**
 * Custom hook for getting verification status
 * Note: Status is now retrieved from talent profile data (twin object) via useAirbankStatus hook
 * This hook is kept for backward compatibility but is no longer used
 * @deprecated Use useAirbankStatus from hooks/useAirbankStatus.ts instead
 */
export const useGetVerificationStatus = (options: {
  key: any[];
  params: { talentId: number };
  options?: any;
}) => {
  // This is a placeholder - actual status is retrieved from talent profile
  // See useAirbankStatus hook in hooks/useAirbankStatus.ts
  return {
    data: null,
    isLoading: false,
    isError: false,
    error: null,
  };
};

// AI DNA List Query Parameters
export interface AITwinDNAQueryParams {
  offset?: number; // Offset of the query. Default is 0
  limit?: number; // Limit of the data. Default is 10
}

// AI DNA Registration Response Type
export interface AITwinDNARegistration {
  id?: number;
  name?: string;
  personality?: string;
  language?: string;
  custom_greetings?: string;
  avatar_url?: string;
  audio_url?: string;
  video_url?: string;
  created_at?: string;
  updated_at?: string;
  [key: string]: any; // Allow for additional fields from API
}

// Dummy AI DNA Registration data - Used as fallback when API returns no data
const DUMMY_AI_DNA_REGISTRATIONS: AITwinDNARegistration[] = [
  {
    id: 1,
    name: "Luna AI",
    personality:
      "Friendly and creative, specializing in music and entertainment",
    language: "en",
    custom_greetings:
      "Hello! I'm Luna, ready to help with your creative projects!",
    avatar_url: "https://i.pravatar.cc/150?img=1",
    audio_url: "https://example.com/audio/luna-ai.mp3",
    video_url: "https://example.com/video/luna-ai.mp4",
    created_at: "2024-01-15T10:30:00.000Z",
    updated_at: "2024-01-15T10:30:00.000Z",
  },
  {
    id: 2,
    name: "Maya Creative",
    personality: "Artistic and focused on design, creativity, and visual arts",
    language: "en",
    custom_greetings:
      "Hi there! I'm Maya, let's create something amazing together!",
    avatar_url: "https://i.pravatar.cc/150?img=3",
    audio_url: "https://example.com/audio/maya-creative.mp3",
    video_url: "https://example.com/video/maya-creative.mp4",
    created_at: "2024-01-20T14:20:00.000Z",
    updated_at: "2024-01-20T14:20:00.000Z",
  },
  {
    id: 3,
    name: "Kai Business",
    personality:
      "Professional and expert in strategy, marketing, and business development",
    language: "en",
    custom_greetings: "Welcome! I'm Kai, here to help you grow your business!",
    avatar_url: "https://i.pravatar.cc/150?img=6",
    audio_url: "https://example.com/audio/kai-business.mp3",
    video_url: "https://example.com/video/kai-business.mp4",
    created_at: "2024-02-01T09:15:00.000Z",
    updated_at: "2024-02-01T09:15:00.000Z",
  },
  {
    id: 4,
    name: "Sage Knowledge",
    personality: "Knowledgeable and specializing in education and learning",
    language: "en",
    custom_greetings: "Hello! I'm Sage, ready to help you learn and grow!",
    avatar_url: "https://i.pravatar.cc/150?img=8",
    audio_url: "https://example.com/audio/sage-knowledge.mp3",
    video_url: "https://example.com/video/sage-knowledge.mp4",
    created_at: "2024-02-10T16:45:00.000Z",
    updated_at: "2024-02-10T16:45:00.000Z",
  },
  {
    id: 5,
    name: "Zara Wellness",
    personality:
      "Wellness-focused, helping with health, fitness, and mindfulness",
    language: "en",
    custom_greetings:
      "Hi! I'm Zara, let's work on your wellness journey together!",
    avatar_url: "https://i.pravatar.cc/150?img=5",
    audio_url: "https://example.com/audio/zara-wellness.mp3",
    video_url: "https://example.com/video/zara-wellness.mp4",
    created_at: "2024-02-15T11:30:00.000Z",
    updated_at: "2024-02-15T11:30:00.000Z",
  },
];

// License Request Types
export interface RequestAITwinDNALicensePayload {
  twin_talent_id: number; // AI Twin ID (talent ID of the AI Twin)
  start_date: string; // ISO 8601 format: "2026-01-12T09:24:59.461Z"
  end_date: string; // ISO 8601 format: "2026-01-12T09:24:59.461Z"
}

// GET endpoint for listing AI DNA registrations - matches /api/v1/airbank/ait-dna
const getAITwinDNAListAPI = (
  queryParams: AITwinDNAQueryParams = {},
): string => {
  const fullParams = {
    ...queryParams,
    offset: queryParams.offset ?? 0,
    limit: queryParams.limit ?? 10, // Default is 10 as per API docs
  };

  const queryString = new URLSearchParams(
    Object.entries(fullParams).reduce(
      (acc, [key, value]) => {
        if (key !== "headers" && value !== undefined && value !== null) {
          acc[key] = String(value);
        }
        return acc;
      },
      {} as Record<string, string>,
    ),
  ).toString();

  return `/airbank/ait-dna?${queryString}`;
};

// License request endpoint - matches /api/v1/airbank/request-ait-dna-license
const requestAITwinDNALicenseEndpoint = () =>
  `/airbank/request-ait-dna-license`;

// Create the GET hook for listing AI DNA registrations
const useGetAITwinDNAListHook = createGetApiHook<
  APIResponseType<AITwinDNARegistration[]>,
  AITwinDNAQueryParams
>(getAITwinDNAListAPI);

/**
 * Custom hook for getting list of AI DNA registrations
 * Endpoint: GET /api/v1/airbank/ait-dna
 * Query Parameters: { offset?: number, limit?: number }
 *
 * This endpoint returns a paginated list of AI DNA registrations.
 * Default offset is 0, default limit is 10.
 *
 * If the API returns no data or fails, dummy data will be used as fallback.
 */
export const useGetAITwinDNAList = (options: {
  key: any[];
  params?: AITwinDNAQueryParams;
  options?: {
    enabled?: boolean;
    retry?: number;
    staleTime?: number;
  };
}) => {
  const { key, params = {}, options: queryOptions } = options;

  const { data, isLoading, isError, error, refetch } = useGetAITwinDNAListHook({
    key: [...key, "airbank-ait-dna-list"],
    params: {
      offset: params.offset ?? 0,
      limit: params.limit ?? 10,
    },
    options: {
      enabled: queryOptions?.enabled !== false,
      retry: queryOptions?.retry ?? 2,
      staleTime: queryOptions?.staleTime ?? 30000, // 30 seconds
    },
  });

  // Use dummy data as fallback when API returns no data or fails
  const registrations = useMemo(() => {
    const apiData = data?.data;
    // If API returns data, use it; otherwise use dummy data
    if (apiData && Array.isArray(apiData) && apiData.length > 0) {
      return apiData;
    }
    // Return dummy data when no data from API
    return DUMMY_AI_DNA_REGISTRATIONS;
  }, [data?.data]);

  return {
    data,
    isLoading,
    isError,
    error,
    refetch,
    // Helper to get the list of registrations (with dummy data fallback)
    registrations,
  };
};

// Create the license request hook
const useRequestAITwinDNALicenseHook = createPostApiHook<
  RequestAITwinDNALicensePayload,
  { headers: { "talent-id": string } },
  APIResponseType<any>
>(requestAITwinDNALicenseEndpoint, {
  isFormDataMethod: false,
});

/**
 * Custom hook for requesting AI Twin DNA license
 * Endpoint: POST /api/v1/airbank/request-ait-dna-license
 * Requires: talent-id header
 * Payload: { twin_talent_id: number, start_date: string (ISO), end_date: string (ISO) }
 *
 * This is used when creating instant cards with AI Twin enabled to request a license
 * for the AI Twin to be used with the card.
 */
export const useRequestAITwinDNALicense = (options: {
  key: any[];
  params: { talentId: number };
}) => {
  const { key, params } = options;
  const { talentId } = params;
  const queryClient = useQueryClient();

  const {
    update: requestLicense,
    isLoading,
    isError,
    error,
  } = useRequestAITwinDNALicenseHook({
    key: [...key, "airbank-request-license"],
    params: {
      headers: {
        "talent-id": talentId.toString(),
      },
    },
  });

  return {
    requestLicense: async (
      payload: RequestAITwinDNALicensePayload,
      callbacks?: {
        onSuccess?: (data: any) => void;
        onError?: (error: any) => void;
      },
    ) => {
      try {
        // Validate required fields
        if (!payload.twin_talent_id || payload.twin_talent_id <= 0) {
          throw new Error("AI Twin ID is required and must be greater than 0");
        }
        if (!payload.start_date) {
          throw new Error("License start date is required");
        }
        if (!payload.end_date) {
          throw new Error("License end date is required");
        }

        // Validate date format (should be ISO 8601)
        const startDate = new Date(payload.start_date);
        const endDate = new Date(payload.end_date);

        if (isNaN(startDate.getTime())) {
          throw new Error(
            "Invalid start date format. Please use ISO 8601 format.",
          );
        }
        if (isNaN(endDate.getTime())) {
          throw new Error(
            "Invalid end date format. Please use ISO 8601 format.",
          );
        }
        if (endDate <= startDate) {
          throw new Error("End date must be after start date");
        }

        // Convert dates to ISO 8601 format if not already
        const formattedPayload: RequestAITwinDNALicensePayload = {
          twin_talent_id: payload.twin_talent_id,
          start_date: startDate.toISOString(),
          end_date: endDate.toISOString(),
        };

        console.log(
          "[Airbank] Requesting license with payload:",
          formattedPayload,
        );

        const result = await requestLicense(formattedPayload, {
          onSuccess: (data: any) => {
            // Invalidate queries to refresh license status
            queryClient.invalidateQueries({
              queryKey: ["talent-profile-airbank-status", talentId],
            });
            queryClient.invalidateQueries({ queryKey: ["user-profile"] });
            callbacks?.onSuccess?.(data);
          },
          onError: (error: any) => {
            console.error("[Airbank] License request failed:", error);
            const errorMessage =
              error?.response?.data?.message ||
              error?.message ||
              "Failed to request AI Twin DNA license. Please try again.";
            callbacks?.onError?.(new Error(errorMessage));
          },
        });
        return result;
      } catch (error: any) {
        console.error("[Airbank] License request error:", error);
        callbacks?.onError?.(error);
        throw error;
      }
    },
    isLoading,
    isError,
    error,
  };
};

// AI Twin List Query Parameters (for /airbank/ait endpoint)
export interface AITwinListQueryParams {
  offset?: number; // Offset of the query. Default is 0
  limit?: number; // Limit of the data. Default is 10
}

// AI Twin Response Type (from /airbank/ait endpoint)
export interface AITwinListItem {
  id?: number;
  talent_id?: number;
  twin?: {
    id?: number;
    name?: string;
    language?: string;
    personality?: string;
    custom_greetings?: string;
    enable_airbank?: boolean;
    avatar_url?: string;
    audio_url?: string;
    video_url?: string;
    airbank_registered_date?: string;
    air_bank_verified_date?: string;
    is_airbank_verified?: boolean;
    [key: string]: any;
  };
  ait_dna?: {
    id?: number;
    dna_id?: string;
    name?: string;
    personality?: string;
    language?: string;
    custom_greetings?: string;
    avatar_url?: string;
    audio_url?: string;
    video_url?: string;
    status?: string;
    [key: string]: any;
  };
  [key: string]: any; // Allow for additional fields from API
}

// GET endpoint for listing AI Twins - matches /api/v1/airbank/ait
const getAITwinListAPI = (queryParams: AITwinListQueryParams = {}): string => {
  const fullParams = {
    ...queryParams,
    offset: queryParams.offset ?? 0,
    limit: queryParams.limit ?? 10, // Default is 10 as per API docs
  };

  const queryString = new URLSearchParams(
    Object.entries(fullParams).reduce(
      (acc, [key, value]) => {
        if (key !== "headers" && value !== undefined && value !== null) {
          acc[key] = String(value);
        }
        return acc;
      },
      {} as Record<string, string>,
    ),
  ).toString();

  return `/airbank/ait?${queryString}`;
};

// Create the GET hook for listing AI Twins
const useGetAITwinListHook = createGetApiHook<
  APIResponseType<AITwinListItem[]>,
  AITwinListQueryParams
>(getAITwinListAPI);

/**
 * Custom hook for getting list of available AI Twins
 * Endpoint: GET /api/v1/airbank/ait
 * Query Parameters: { offset?: number, limit?: number }
 *
 * This endpoint returns a paginated list of available AI Twins that can be used
 * when creating instant cards with AI Twin integration.
 * Default offset is 0, default limit is 10.
 */
export const useGetAITwinList = (options: {
  key: any[];
  params?: AITwinListQueryParams;
  options?: {
    enabled?: boolean;
    retry?: number;
    staleTime?: number;
  };
}) => {
  const { key, params = {}, options: queryOptions } = options;

  const { data, isLoading, isError, error, refetch } = useGetAITwinListHook({
    key: [...key, "airbank-ait-list"],
    params: {
      offset: params.offset ?? 0,
      limit: params.limit ?? 10,
    },
    options: {
      enabled: queryOptions?.enabled !== false,
      retry: queryOptions?.retry ?? 2,
      staleTime: queryOptions?.staleTime ?? 30000, // 30 seconds
    },
  });

  // Transform API response to EnabledAITwin format
  const aiTwins = useMemo(() => {
    const apiData = data?.data;
    if (!apiData || !Array.isArray(apiData)) {
      return [];
    }

    return apiData.map((item: AITwinListItem) => {
      const talentId = Number(item.talent_id || item.id || 0);
      // Use ait_dna.id as the unique identifier for the AI Twin, fallback to twin.id, then talent_id
      // This ensures each AI Twin has a unique ID, not just the talent ID
      const id = Number(
        item.ait_dna?.id || item.twin?.id || item.id || item.talent_id || 0,
      );

      // Get name from ait_dna.name (priority), fallback to twin.name, then default
      const name = item.ait_dna?.name || item.twin?.name || "AI Twin";

      // Get avatar from ait_dna.avatar_url (priority), fallback to twin.avatar_url
      const avatar =
        item.ait_dna?.avatar_url || item.twin?.avatar_url || undefined;

      // Get language from ait_dna.language (priority), fallback to twin.language
      const language = item.ait_dna?.language || item.twin?.language || "en";

      // Get verification status from twin.is_airbank_verified
      const is_airbank_verified = item.twin?.is_airbank_verified ?? false;

      // Get enable_airbank from twin.enable_airbank
      const enable_airbank = item.twin?.enable_airbank ?? false;

      // Get description from ait_dna or twin personality
      const description =
        item.ait_dna?.personality || item.twin?.personality || undefined;

      return {
        id,
        talentId,
        name,
        aiTwinName: name,
        avatar,
        description,
        language,
        enable_airbank,
        is_airbank_verified,
      };
    });
  }, [data?.data]);

  return {
    data,
    isLoading,
    isError,
    error,
    refetch,
    aiTwins, // Transformed list of AI Twins in EnabledAITwin format
  };
};

/** Single row from GET /taas-shot/logs (shape may vary by backend). */
export type AitDnaImageUsageLogEntry = {
  id?: string | number;
  /** TAAS shot / uploaded image this log refers to */
  taas_shot_id?: number;
  occurred_at?: string;
  created_at?: string;
  used_at?: string;
  timestamp?: string;
  actor_name?: string;
  actor_display_name?: string;
  user_name?: string;
  username?: string;
  actor_username?: string;
  user_id?: number;
  actor_user_id?: number;
  action?: string;
  event_type?: string;
  event?: string;
  description?: string;
  details?: string;
  context?: string;
  image_url?: string;
  source_image_url?: string;
  reference_image_url?: string;
};

export function normalizeAitDnaImageUsageLogsPayload(
  body: { data?: unknown } | undefined,
): AitDnaImageUsageLogEntry[] {
  if (!body?.data) return [];
  const raw = body.data;
  const asRows = (arr: unknown): AitDnaImageUsageLogEntry[] =>
    Array.isArray(arr)
      ? (arr.filter(
          (row): row is AitDnaImageUsageLogEntry =>
            row != null && typeof row === "object",
        ) as AitDnaImageUsageLogEntry[])
      : [];
  if (Array.isArray(raw)) return asRows(raw);
  if (typeof raw === "object" && raw !== null) {
    const o = raw as Record<string, unknown>;
    if (Array.isArray(o.logs)) return asRows(o.logs);
    if (Array.isArray(o.items)) return asRows(o.items);
    if (Array.isArray(o.results)) return asRows(o.results);
  }
  return [];
}

/** Query params for GET /api/v1/taas-shot/logs (Swagger). */
export type TaasShotLogsQueryParams = {
  talent_id: number;
  taas_shot_id?: number;
  offset?: number;
  limit?: number;
};

const getTaasShotLogsAPI = (queryParams: TaasShotLogsQueryParams): string => {
  const entries: Record<string, string> = {
    talent_id: String(queryParams.talent_id),
    offset: String(queryParams.offset ?? 0),
    limit: String(queryParams.limit ?? 10),
  };
  if (
    queryParams.taas_shot_id != null &&
    !Number.isNaN(Number(queryParams.taas_shot_id))
  ) {
    entries.taas_shot_id = String(queryParams.taas_shot_id);
  }
  const queryString = new URLSearchParams(entries).toString();
  return `/taas-shot/logs?${queryString}`;
};

const useGetTaasShotLogsHook = createGetApiHook<
  APIResponseType<
    | AitDnaImageUsageLogEntry[]
    | { logs?: AitDnaImageUsageLogEntry[]; items?: AitDnaImageUsageLogEntry[] }
  >,
  TaasShotLogsQueryParams
>(getTaasShotLogsAPI);

/**
 * TAAS Shot usage logs (who used which uploaded shot / AI-DNA image).
 * Endpoint: GET /api/v1/taas-shot/logs
 * Query: talent_id (required), taas_shot_id (optional), offset, limit
 */
export const useGetAitDnaImageUsageLogs = (options: {
  key: any[];
  params: {
    talentId: number;
    /** Filter to a single shot / image id */
    taasShotId?: number;
    offset?: number;
    /** API default 10; we default higher for the studio log view */
    limit?: number;
  };
  options?: {
    enabled?: boolean;
    retry?: number;
    staleTime?: number;
  };
}) => {
  const { key, params, options: queryOptions } = options;
  const { talentId, taasShotId, offset, limit } = params;

  const queryParams: TaasShotLogsQueryParams = {
    talent_id: talentId,
    offset: offset ?? 0,
    limit: limit ?? 50,
    ...(taasShotId != null ? { taas_shot_id: taasShotId } : {}),
  };

  const { data, isLoading, isError, error, refetch } = useGetTaasShotLogsHook({
    key: [
      ...key,
      "taas-shot-logs",
      talentId,
      taasShotId ?? "all",
      queryParams.offset,
      queryParams.limit,
    ],
    params: queryParams,
    options: {
      enabled: queryOptions?.enabled !== false && !!talentId,
      retry: queryOptions?.retry ?? 1,
      staleTime: queryOptions?.staleTime ?? 30000,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
    },
  });

  const logs = normalizeAitDnaImageUsageLogsPayload(data as { data?: unknown });

  return {
    data,
    logs,
    isLoading,
    isError,
    error,
    refetch,
  };
};
